/**
 * DICOM attribute tag: a (group, element) pair packed into one unsigned
 * 32-bit number as `(group << 16) | element`.
 *
 * The packed form is the identity used throughout the engine because it is
 * both the map key and the sort key: PS3.5 requires elements to be written in
 * ascending tag order, and numeric ordering of the packed value is exactly
 * that order. Group 0xFFFE (item delimiters) packs above 2^31, so every
 * construction coerces to unsigned with `>>> 0`.
 */
export type Tag = number;

/** Item start delimiter, (FFFE,E000). */
export const ITEM_TAG: Tag = 0xfffee000;
/** Item end delimiter for an undefined-length item, (FFFE,E00D). */
export const ITEM_DELIMITATION_TAG: Tag = 0xfffee00d;
/** Sequence end delimiter for an undefined-length sequence, (FFFE,E0DD). */
export const SEQUENCE_DELIMITATION_TAG: Tag = 0xfffee0dd;

/** (7FE0,0010) PixelData. */
export const PIXEL_DATA_TAG: Tag = 0x7fe00010;
/** (0008,0005) SpecificCharacterSet. */
export const SPECIFIC_CHARACTER_SET_TAG: Tag = 0x00080005;
/** (0002,0000) FileMetaInformationGroupLength. */
export const FILE_META_GROUP_LENGTH_TAG: Tag = 0x00020000;
/** (0002,0001) FileMetaInformationVersion. */
export const FILE_META_INFORMATION_VERSION_TAG: Tag = 0x00020001;
/** (0002,0002) MediaStorageSOPClassUID. */
export const MEDIA_STORAGE_SOP_CLASS_UID_TAG: Tag = 0x00020002;
/** (0002,0003) MediaStorageSOPInstanceUID. */
export const MEDIA_STORAGE_SOP_INSTANCE_UID_TAG: Tag = 0x00020003;
/** (0002,0010) TransferSyntaxUID. */
export const TRANSFER_SYNTAX_UID_TAG: Tag = 0x00020010;
/** (0002,0012) ImplementationClassUID. */
export const IMPLEMENTATION_CLASS_UID_TAG: Tag = 0x00020012;
/** (0002,0013) ImplementationVersionName. */
export const IMPLEMENTATION_VERSION_NAME_TAG: Tag = 0x00020013;

/** (0008,0016) SOPClassUID. */
export const SOP_CLASS_UID_TAG: Tag = 0x00080016;
/** (0008,0018) SOPInstanceUID. */
export const SOP_INSTANCE_UID_TAG: Tag = 0x00080018;

/** The length field value that marks an element as undefined-length. */
export const UNDEFINED_LENGTH = 0xffffffff;

export function makeTag(group: number, element: number): Tag {
    return (((group & 0xffff) << 16) | (element & 0xffff)) >>> 0;
}

export function tagGroup(tag: Tag): number {
    return (tag >>> 16) & 0xffff;
}

export function tagElement(tag: Tag): number {
    return tag & 0xffff;
}

/** "00100010" — the unpunctuated, uppercase form used as a dictionary key. */
export function formatTag(tag: Tag): string {
    return (tag >>> 0).toString(16).toUpperCase().padStart(8, '0');
}

/** "(0010,0010)" — the punctuated form used for display and in dcmjs keys. */
export function punctuateTag(tag: Tag): string {
    const hex = formatTag(tag);
    return `(${hex.slice(0, 4)},${hex.slice(4)})`;
}

/**
 * Parses "00100010" or "(0010,0010)" to a Tag.
 *
 * Returns undefined rather than NaN for anything else: a NaN tag silently
 * becomes a distinct-but-unusable map key, which turns a typo into a missing
 * attribute instead of an error.
 */
export function parseTag(value: string): Tag | undefined {
    const hex = value.replace(/[(),]/g, '').trim();
    if (!/^[0-9a-fA-F]{8}$/.test(hex)) {
        return undefined;
    }
    return parseInt(hex, 16) >>> 0;
}

/**
 * True for a group-length element, (gggg,0000).
 *
 * PS3.5 retired every group length but (0002,0000). The others are carried
 * through a read/write cycle untouched, along with their original values, so
 * that a file which has them still round-trips; only the file meta group
 * length is recomputed, because it describes bytes the writer just produced.
 */
export function isGroupLengthTag(tag: Tag): boolean {
    return tagElement(tag) === 0x0000;
}

/** True for a private attribute — odd group number, per PS3.5 section 7.8. */
export function isPrivateTag(tag: Tag): boolean {
    return (tagGroup(tag) & 1) === 1;
}

/** True for a group-0002 file meta element. */
export function isFileMetaTag(tag: Tag): boolean {
    return tagGroup(tag) === 0x0002;
}
